
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Crown } from "lucide-react";
import { useIncognitoBrowsing } from "@/hooks/useIncognitoBrowsing";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";

const PrivacySection = () => {
  const navigate = useNavigate();
  const { isIncognitoEnabled, hasIncognitoAccess, toggleIncognito, loading } = useIncognitoBrowsing();

  const handleIncognitoToggle = async (enabled: boolean) => {
    if (!hasIncognitoAccess) {
      toast({
        title: "Upgrade Required",
        description: "Incognito browsing is only available with Alpha∞ subscription",
        variant: "destructive",
      });
      navigate("/pricing");
      return;
    }

    const success = await toggleIncognito(enabled);
    if (success) {
      toast({
        title: enabled ? "Incognito Mode Enabled" : "Incognito Mode Disabled",
        description: enabled 
          ? "Your profile views will now be anonymous" 
          : "Your profile views will now show your identity",
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to update incognito setting",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="bg-gray-900/80 border-gray-600">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-ice-white">
          <Eye className="w-5 h-5 text-pink-500" />
          Privacy
        </CardTitle>
        <CardDescription className="text-gray-300">
          Control who can see your profile and information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="discovery">Show me in Discovery</Label>
            <p className="text-sm text-gray-500">Let others find your profile</p>
          </div>
          <Switch id="discovery" defaultChecked />
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="distance">Show distance</Label>
            <p className="text-sm text-gray-500">Display your distance to others</p>
          </div>
          <Switch id="distance" defaultChecked />
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="age">Show age</Label>
            <p className="text-sm text-gray-500">Display your age on your profile</p>
          </div>
          <Switch id="age" defaultChecked />
        </div>
        
        {/* Incognito Browsing Section */}
        <div className="border-t border-gray-700 pt-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Label htmlFor="incognito" className="flex items-center gap-2">
                  <EyeOff className="w-4 h-4" />
                  Incognito Browsing
                </Label>
                {!hasIncognitoAccess && (
                  <Crown className="w-4 h-4 text-alpha-orange" />
                )}
              </div>
              <p className="text-sm text-gray-500">
                {hasIncognitoAccess 
                  ? "Browse profiles anonymously without revealing your identity"
                  : "Alpha∞ subscribers can browse profiles anonymously"
                }
              </p>
            </div>
            {hasIncognitoAccess ? (
              <Switch 
                id="incognito" 
                checked={isIncognitoEnabled}
                onCheckedChange={handleIncognitoToggle}
                disabled={loading}
              />
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate("/pricing")}
                className="border-alpha-orange text-alpha-orange hover:bg-alpha-orange hover:text-black"
              >
                Upgrade
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PrivacySection;
